import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import MyNavbar from "../../../components/customer/MyNavbar";
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FaMapMarkerAlt,
  FaUsers,
  FaArrowLeft,
  FaCheckCircle,
  FaSearchPlus
} from 'react-icons/fa';
import '../../../assets/css/customer/venues/VenueDetailpage.css';

const features = [
  'Air Conditioned Halls',
  'Parking Available',
  'Catering Services',
  'Photography Allowed',
  'Decoration Services',
  'Sound System',
  'Bridal Room',
  'Backup Generator'
];

const VenueDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const venue = location.state?.venue;

  const gallery = venue?.gallery ? JSON.parse(venue.gallery) : [];
  const venueImages = gallery.length > 0
    ? gallery.map((url, i) => ({ id: i, url, caption: venue.venueName }))
    : [{ id: 0, url: venue?.coverImage, caption: venue?.venueName }];

  const [currentSlide, setCurrentSlide] = useState(0);
  const [bookingLoading, setBookingLoading] = useState(false);
  const autoRef = useRef(null);

  // Auto slide
  useEffect(() => {
    autoRef.current = setInterval(() =>
      setCurrentSlide(p => (p + 1) % venueImages.length), 4000);
    return () => clearInterval(autoRef.current);
  }, [venueImages.length]);

  const goToSlide = (index) => {
    clearInterval(autoRef.current);
    setCurrentSlide(index);
    autoRef.current = setInterval(() =>
      setCurrentSlide(p => (p + 1) % venueImages.length), 4000);
  };

  const prevSlide = () => goToSlide((currentSlide - 1 + venueImages.length) % venueImages.length);
  const nextSlide = () => goToSlide((currentSlide + 1) % venueImages.length);

  // Price format
  const formatPrice = (val) => {
    if (!val) return '—';
    const num = Number(String(val).replace(/,/g, ''));
    if (isNaN(num)) return val;
    return 'PKR ' + num.toLocaleString('en-PK');
  };

  // ✅ NEW: Book This Venue se pehle login check — same localStorage key
  // ('loggedInCustomer') jo MyBookings.js aur BookingDetails.js mein use ho raha hai.
  const handleBookVenue = () => {
    setBookingLoading(true);
    const loggedInCustomer = JSON.parse(localStorage.getItem('loggedInCustomer'));

    setTimeout(() => {
      if (loggedInCustomer) {
        navigate("/bookingdetails", { state: { venue } });
      } else {
        navigate("/customer/login", { state: { redirectTo: "/bookingdetails", venue } });
      }
      setBookingLoading(false);
    }, 600);
  };

  if (!venue) {
    return (
      <>
        <MyNavbar />
        <div className="vdp-page">
          <Container>
            <p>No venue selected. Please go back and choose a venue.</p>
            <button className="vdp-back-btn" onClick={() => navigate("/venues")}>
              <FaArrowLeft className="vdp-back-icon" />
              Back to Venues
            </button>
          </Container>
        </div>
      </>
    );
  }

  return (
    <>
      <MyNavbar />
      <div className="vdp-page">
        <Container>

          <Row className="vdp-back-row">
            <Col xs={12}>
              <button className="vdp-back-btn" onClick={() => navigate(-1)}>
                <FaArrowLeft className="vdp-back-icon" />
                Back to Venues
              </button>
            </Col>
          </Row>

          <Row className="vdp-hero-row">
            <Col xs={12}>
              <div className="vdp-carousel-wrapper">
                <div className="vdp-carousel">
                  {venueImages.map((img, index) => (
                    <div
                      key={img.id}
                      className={`vdp-carousel-slide ${index === currentSlide ? 'vdp-carousel-slide-active' : ''}`}
                    >
                      <img src={img.url} alt={img.caption} className="vdp-carousel-img" />
                      <span className="vdp-carousel-zoom-icon">
                        <FaSearchPlus />
                      </span>
                      <div className="vdp-carousel-caption">{img.caption}</div>
                    </div>
                  ))}

                  {/* Prev / Next buttons */}
                  {venueImages.length > 1 && (
                    <>
                      <button className="vdp-carousel-btn vdp-carousel-btn--prev" onClick={prevSlide}>&#8249;</button>
                      <button className="vdp-carousel-btn vdp-carousel-btn--next" onClick={nextSlide}>&#8250;</button>
                    </>
                  )}

                  <div className="vdp-hero-city">{venue.city}</div>

                  <div className="vdp-carousel-counter">
                    {currentSlide + 1} / {venueImages.length}
                  </div>
                </div>

                <div className="vdp-carousel-dots">
                  {venueImages.map((_, index) => (
                    <button
                      key={index}
                      className={`vdp-carousel-dot ${index === currentSlide ? 'vdp-carousel-dot-active' : ''}`}
                      onClick={() => goToSlide(index)}
                    />
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          <Row className="vdp-content-row g-4">

            <Col xs={12} lg={8}>
              <h1 className="vdp-venue-name">{venue.venueName}</h1>

              <div className="vdp-info-row">
                <span className="vdp-info-item">
                  <FaMapMarkerAlt className="vdp-info-icon" />
                  {venue.location}, {venue.city}
                </span>
                <span className="vdp-info-item">
                  <FaUsers className="vdp-info-icon" />
                  Capacity: {venue.capacity} guests
                </span>
              </div>

              <div className="vdp-description-box">
                <h4 className="vdp-sub-heading">About This Venue</h4>
                <p className="vdp-description">
                  {venue.venueName} is one of the most prestigious wedding venues located in {venue.location}, {venue.city}.
                  With a capacity of {venue.capacity} guests, it offers a luxurious setting for your special day.
                  Our team of experienced professionals ensures that every detail of your event is perfectly executed.
                  From elegant decor to world-class catering, we provide everything you need for a memorable celebration.
                </p>
              </div>

              <div className="vdp-features-box">
                <h4 className="vdp-sub-heading">Venue Services</h4>
                <Row className="g-2">
                  {features.map((feature, index) => (
                    <Col key={index} xs={12} sm={6}>
                      <div className="vdp-feature-item">
                        <FaCheckCircle className="vdp-check-icon" />
                        <span>{feature}</span>
                      </div>
                    </Col>
                  ))}
                </Row>
              </div>
            </Col>

            <Col xs={12} lg={4}>
              <div className="vdp-booking-card">

                <div className="vdp-price-section">
                  <h4 className="vdp-price-label">Price Range</h4>
                  <p className="vdp-price">{formatPrice(venue.priceRange)}</p>
                </div>

                <hr className="vdp-card-divider" />
                <hr className="vdp-card-divider" />

                <button
                  className="vdp-book-btn"
                  disabled={bookingLoading}
                  onClick={handleBookVenue}
                >
                  {bookingLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Loading...
                    </>
                  ) : (
                    'Book This Venue'
                  )}
                </button>

              </div>
            </Col>

          </Row>
        </Container>
      </div>
    </>
  );
};

export default VenueDetailPage;